"use client";

import { useState } from "react";
import Link from "next/link";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { menu } from "../utils/data";
import { LogoSvgImg } from "./svgFiles";
import { usePathname } from "next/navigation";
import { AnimatePresence, motion } from "framer-motion";

const MenuItem = ({
  label,
  link,
  submenu,
}: {
  label: string;
  link?: string;
  submenu?: { label: string; link: string }[];
}) => {
  const path = usePathname();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div
      className="relative group h-full"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <Link
        href={link || "#"}
        className={`${
          path === link && "text-primaryColor font-[600]"
        } group font-[500] text-[16.5px] flex items-center gap-1 h-full hover:text-primaryColor hover:font-[600] transition duration-500 ease-in-out`}
      >
        {label}
        {submenu && (
          <ChevronDownIcon className="h-4 w-4 text-[#b1b1b1] transition-transform duration-300 group-hover:rotate-180" />
        )}
      </Link>

      {/* Submenu Items */}
      {submenu && isOpen && (
        <div className="absolute left-0 w-[17rem] bg-white shadow-lg rounded-lg py-3">
          {submenu.map((subItem, index) => (
            <Link
              key={index}
              href={subItem.link}
              className="block px-4 py-2 text-gray-800 hover:bg-gray-100 text-[16px] tracking-wide font-medium transition duration-300 ease-in-out"
            >
              {subItem.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default function Navbar() {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  return (
    <div className="">
      <div className="bg-[#000000] h-[90px] w-full fixed px-5 xl:px-[6rem] z-30">
        <div className="max-w-[92rem] text-white mx-auto flex items-center justify-between h-full">
          <Link href="/">
            <LogoSvgImg className="w-[120px]" />
          </Link>
          <div className="hidden xl:flex items-center gap-4 h-full">
            {menu.map((item, index) => (
              <MenuItem key={index} {...item} />
            ))}
          </div>
          <div className="hidden xl:flex items-center gap-4">
            <Link
              href="/contact"
              className="px-7 py-2 rounded-full bg-[#CC9932] text-[16px] font-[500]"
            >
              Contact Us
            </Link>
          </div>
          <div className="xl:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="relative flex flex-col justify-center items-center w-12 h-12"
            >
              <motion.div
                initial={{ rotate: 0, opacity: 1, y: 0 }}
                animate={isOpen ? { rotate: 45, y: 3 } : { rotate: 0, y: 0 }}
                transition={{ duration: 0.3 }}
                className="w-6 h-[3px] bg-gray-300"
              />
              <motion.div
                initial={{ rotate: 0, opacity: 1, y: 0 }}
                animate={isOpen ? { rotate: -45, y: -4 } : { rotate: 0, y: 0 }}
                transition={{ duration: 0.3 }}
                className="w-6 h-[3px] bg-gray-300 mt-[0.27rem]"
              />
            </button>
          </div>
        </div>
      </div>
      <div className="w-full h-[90px]">j</div>

      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={isOpen ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className={`bg-black text-white z-20 fixed w-full h-screen py-10 px-6 xl:hidden overflow-y-auto ${
          !isOpen ? "pointer-events-none" : "pointer-events-auto"
        }`}
      >
        <nav className="">
          {menu.map((item, index) => (
            <div key={index} className="mb-3">
              {/* Main Menu Item */}
              <Link
                href={item?.link as string}
                className="w-full text-left font-bold text-[23px] sm:text-[25px] py-2 border-b-[0.1px] border-gray-200 flex items-center justify-between gap-3 hover:text-primaryColor"
                onClick={() =>
                  setOpenMenu(openMenu === item.label ? null : item.label)
                }
              >
                {item.label}

                {/* Show Chevron for Submenu */}
                {item.submenu && (
                  <motion.div
                    animate={{ rotate: openMenu === item.label ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ChevronDownIcon className="w-6 font-bold" />
                  </motion.div>
                )}
              </Link>

              {/* Submenu Animation */}
              <AnimatePresence>
                {item.submenu && openMenu === item.label && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className=" mt-2 space-y-2"
                  >
                    {item.submenu.map((sub, subIndex) => (
                      <Link
                        key={subIndex}
                        href={sub.link}
                        className="block text-gray-300 hover:text-white text-lg py-3 border-b-[0.1px] border-gray-200"
                      >
                        {sub.label}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </nav>
        <div className="flex justify-center mt-8 pb-[6rem]">
          <Link
            href="/contact"
            className="px-7 py-2 rounded-full bg-[#CC9932] text-[16px] font-[500]"
          >
            Contact Us
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
